import React, { useEffect, useState, useRef, useCallback } from "react";
import { Navigation, AlertTriangle, Loader2, Clock, MapPin } from "lucide-react";
import { api } from "../lib/api";

const POLL_MS = 20000;  // matches backend's 45s cache — every 2nd poll is a fresh fetch

/**
 * Decode a Google encoded polyline string into [{lat, lng}, …].
 *
 * Implementation of Google's polyline algorithm — pulled out into this
 * small helper so the SmartMap component stays free of Directions-specific
 * concerns. See
 * https://developers.google.com/maps/documentation/utilities/polylinealgorithm
 */
export const decodePolyline = (encoded) => {
  if (!encoded) return [];
  const points = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  while (index < encoded.length) {
    let b;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += dlat;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = result & 1 ? ~(result >> 1) : result >> 1;
    lng += dlng;

    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
};

/**
 * In-app turn-by-turn directions for the driver's active ride leg.
 *
 * Lifecycle:
 *   • Fetches /api/driver/directions/{rideId} every 20s while mounted.
 *   • Surfaces the next maneuver + remaining distance/ETA in a tight panel.
 *   • Calls back to the parent with the decoded polyline (via `onRoute`)
 *     so the parent can pass it to SmartMap without each component
 *     having to know about the other.
 *
 * Failure handling:
 *   • Backend returns `ok: false` whenever the Directions API call fails
 *     for any reason (missing key, REQUEST_DENIED, network). The panel
 *     stays mounted and shows a clean fallback message — the "Open in
 *     Google Maps" link in the parent active-ride card is the safety net.
 */
export const DirectionsPanel = ({ rideId, phase, stopIndex = 0, onRoute, onNav, previewDropoff = false, refreshSignal = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const onRouteRef = useRef(onRoute);
  onRouteRef.current = onRoute;
  const onNavRef = useRef(onNav);
  onNavRef.current = onNav;
  // Whether we've ever delivered a good route. If so, a later transient failure
  // (delayed refresh, momentary Google error) must NOT wipe the route/steps —
  // client-side navigation keeps progressing on the last-good data (req #9).
  const hadGoodRef = useRef(false);

  // Memoize the fetcher so the polling interval keeps a stable identity
  const refresh = useCallback(async () => {
    if (!rideId) return;
    try {
      const url = previewDropoff
        ? `/driver/directions/${rideId}?preview_dropoff=1`
        : `/driver/directions/${rideId}`;
      const { data: payload } = await api.get(url);
      setData(payload);
      setErr(null);
      if (payload?.ok && payload.polyline) {
        const points = decodePolyline(payload.polyline);
        hadGoodRef.current = true;
        onRouteRef.current?.(points);
        // Surface the next maneuver + live ETA/distance for the in-app
        // navigation banner. Origin is the driver's live position, so
        // `next_step` is always the upcoming turn and self-updates.
        onNavRef.current?.({
          nextStep: payload.next_step || null,
          steps: Array.isArray(payload.steps) ? payload.steps : [],
          distanceText: payload.distance_text || null,
          durationText: payload.duration_text || null,
          durationSeconds: Number.isFinite(payload.duration_seconds) ? payload.duration_seconds : null,
          distanceMeters: Number.isFinite(payload.distance_meters) ? payload.distance_meters : null,
        });
      } else if (!hadGoodRef.current) {
        // Only clear when we never had a good route — otherwise keep last-good
        // so client navigation continues uninterrupted (req #9).
        onRouteRef.current?.(null);
        onNavRef.current?.(null);
      }
    } catch (e) {
      // 409 means "no directions for this phase" — not really an error
      const msg = e?.response?.data?.detail || e?.message || "directions_failed";
      setErr(msg);
      if (!hadGoodRef.current) {
        onRouteRef.current?.(null);
        onNavRef.current?.(null);
      }
    } finally {
      setLoading(false);
    }
  }, [rideId, previewDropoff]);

  // Reset the "had good route" guard whenever the ride changes so a new ride
  // never inherits the previous ride's last-good route.
  useEffect(() => { hadGoodRef.current = false; }, [rideId]);

  useEffect(() => {
    setLoading(true);
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
    // refresh is stable; rideId/phase changes drive a re-fetch via the
    // outer state changing or the polling interval picking up the new phase.
    // `stopIndex` is included so advancing a multi-stop journey re-fetches
    // the next-leg route immediately instead of waiting for the next poll.
  }, [refresh, phase, stopIndex]);

  // Immediate re-fetch when the parent bumps `refreshSignal` (off-route
  // rerouting). The 20s poll already self-heals from live GPS; this just makes
  // recovery feel instant. Skips the initial 0 so it doesn't double-fetch.
  const didReroute = useRef(false);
  useEffect(() => {
    if (!didReroute.current) { didReroute.current = true; return; }
    if (refreshSignal) refresh();
  }, [refreshSignal, refresh]);

  if (loading && !data) {
    return (
      <div className="border border-border bg-white px-3 py-2 text-xs text-muted-foreground flex items-center gap-2" data-testid="directions-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Fetching turn-by-turn directions…
      </div>
    );
  }

  if (err) {
    return (
      <div className="border border-[#F59E0B] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]" data-testid="directions-error">
        Directions unavailable: {err}. Use Google Maps below.
      </div>
    );
  }

  if (!data?.ok) {
    return (
      <div className="border border-[#F59E0B] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E] flex items-start gap-1.5" data-testid="directions-fallback">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <div>
          In-app directions are temporarily offline ({data?.error || "no_route"}).
          Use the <strong>NAVIGATE</strong> button below for Google Maps voice nav.
        </div>
      </div>
    );
  }

  const next = data.next_step;
  const kind = data.destination_kind;
  const isStop = kind === "stop" || kind === "stop_preview";
  const isPreview = kind === "dropoff_preview" || kind === "stop_preview";
  const destLabel = isStop
    ? `STOP ${data.stop_number || ""}`.trim()
    : kind === "pickup" ? "PICKUP" : "DROP-OFF";
  const headerLabel = isPreview ? "NEXT LEG · PREVIEW" : "TURN-BY-TURN";

  return (
    <div
      className={`border ${isPreview ? "border-[#FFC107] bg-[#FFFBEB]" : "border-border bg-white"}`}
      data-testid="directions-panel"
      data-preview={isPreview ? "true" : "false"}
    >
      <div className={`flex items-center justify-between px-3 py-1.5 border-b ${isPreview ? "border-[#FFC107]/40 bg-[#FFC107]/15" : "border-border bg-secondary/30"}`}>
        <div className="label-mini text-muted-foreground flex items-center gap-1">
          <Navigation className="w-3 h-3" /> {headerLabel} → {destLabel}
        </div>
        <div className="flex items-center gap-3 text-[11px] font-mono-tab">
          <span className="flex items-center gap-1" data-testid="directions-eta">
            <Clock className="w-3 h-3 text-muted-foreground" />
            {data.duration_text}
          </span>
          <span className="flex items-center gap-1" data-testid="directions-distance">
            <MapPin className="w-3 h-3 text-muted-foreground" />
            {data.distance_text}
          </span>
        </div>
      </div>

      {(isPreview || isStop) && data.destination_address && (
        <div className={`px-3 py-1.5 text-xs border-b truncate ${isPreview ? "text-[#92400E] border-[#FFC107]/40" : "text-[#002FA7] border-border"}`} data-testid="directions-preview-address">
          → {data.destination_address}
        </div>
      )}

      {next && (
        <div className="px-3 py-2" data-testid="directions-next-step">
          <div className="text-sm font-semibold text-foreground leading-tight">
            {next.instruction}
          </div>
          <div className="label-mini text-muted-foreground mt-0.5">
            in {next.distance_text}
            {next.maneuver ? ` · ${next.maneuver.replaceAll("-", " ")}` : ""}
          </div>
        </div>
      )}

      {data.steps?.length > 1 && (
        <details className="border-t border-border" data-testid="directions-step-list">
          <summary className="px-3 py-1.5 text-[11px] text-muted-foreground cursor-pointer hover:bg-secondary/50">
            View all {data.steps.length} steps
          </summary>
          <ol className="px-3 py-2 space-y-1 text-xs">
            {data.steps.map((s, i) => (
              <li key={`${s.instruction}-${i}`} className="flex gap-2" data-testid={`directions-step-${i}`}>
                <span className="text-muted-foreground font-mono-tab w-5">{i + 1}.</span>
                <div className="flex-1">
                  <div>{s.instruction}</div>
                  <div className="label-mini text-muted-foreground">
                    {s.distance_text}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </details>
      )}

      {data.cached && (
        <div className="px-3 py-1 text-[10px] text-muted-foreground border-t border-border" data-testid="directions-cache-badge">
          cached · refreshes every {POLL_MS / 1000}s
        </div>
      )}
    </div>
  );
};
